import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InventoryIcon from '@mui/icons-material/Inventory';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';
import Layout from '../../components/layout/Layout';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import topSellingProductsService from '../../services/topSellingProductsService';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';

const TopSellingProductsReport = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    totalProducts: 0,
    totalQuantitySold: 0,
    totalRevenue: 0,
    products: []
  });
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Load locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);

  // Fetch report data when filters change
  useEffect(() => {
    fetchReportData();
  }, [selectedLocation, startDate, endDate]);

  const fetchLocations = async () => {
    try {
      const response = await axiosInstance.get(`${API_CONFIG.ENDPOINTS.LOCATIONS}?size=1000`);
      const locationsList = response.data.content || [];
      
      // Remove duplicates based on name (case-insensitive)
      const uniqueLocations = [];
      const seenNames = new Set();
      
      locationsList.forEach(location => {
        const normalizedName = location.name?.toLowerCase().trim();
        if (normalizedName && !seenNames.has(normalizedName)) {
          seenNames.add(normalizedName);
          uniqueLocations.push(location);
        }
      });
      
      setLocations(uniqueLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load locations');
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const params = {
        startDate,
        endDate
      };
      if (selectedLocation) {
        params.locationId = selectedLocation;
      }

      const response = await topSellingProductsService.getTopSellingProducts(
        selectedLocation || null,
        startDate,
        endDate
      );
      
      setReportData({
        totalProducts: response.totalProducts || 0,
        totalQuantitySold: response.totalQuantitySold || 0,
        totalRevenue: response.totalRevenue || 0,
        products: response.products || []
      });
    } catch (error) {
      console.error('Error fetching top selling products report:', error);
      toast.error('Failed to load top selling products report');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (product) => {
    try {
      setLoadingDetails(true);
      setSelectedProduct(product);
      setDetailsDialogOpen(true);
      
      const details = await topSellingProductsService.getProductInvoiceDetails(
        product.productId,
        selectedLocation || null,
        startDate,
        endDate
      );
      
      setInvoiceDetails(details || []);
    } catch (error) {
      console.error('Error fetching product invoice details:', error);
      toast.error('Failed to load product invoice details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedProduct(null);
    setInvoiceDetails([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleExportToExcel = () => {
    try {
      setIsExporting(true);

      const wb = XLSX.utils.book_new();
      const ws = {};
      
      ws['!cols'] = [
        { wch: 5 },   // S.No
        { wch: 30 },  // Product Name
        { wch: 15 },  // SKU
        { wch: 20 },  // Category
        { wch: 12 },  // Qty Sold
        { wch: 16 },  // Total Amount
      ];
      
      let currentRow = 1;
      
      const titleText = `Top Selling Products Report (${startDate} to ${endDate})`;
      XLSX.utils.sheet_add_aoa(ws, [[titleText]], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 16, color: { rgb: "1F4E79" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = [{ s: { c: 0, r: currentRow - 1 }, e: { c: 5, r: currentRow - 1 } }];
      currentRow += 2;
      
      XLSX.utils.sheet_add_aoa(ws, [['Generated on:', new Date().toLocaleString()]], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = { font: { bold: true } };
      currentRow += 1;
      
      const activeFilters = [];
      if (selectedLocation) {
        const location = locations.find(l => l.id.toString() === selectedLocation.toString());
        if (location) activeFilters.push(`Location: ${location.name}`);
        else if (selectedLocation === 'warehouse') activeFilters.push('Location: Warehouse');
        else activeFilters.push('Location: All Locations');
      } else {
        activeFilters.push('Location: All Locations');
      }
      
      if (activeFilters.length > 0) {
        XLSX.utils.sheet_add_aoa(ws, [['Filters:', activeFilters.join(', ')]], { origin: `A${currentRow}` });
        ws[`A${currentRow}`].s = { font: { bold: true } };
        currentRow += 1;
      }
      currentRow += 1;
      
      XLSX.utils.sheet_add_aoa(ws, [['SUMMARY']], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = ws[`!merges`] || [];
      ws[`!merges`].push({ s: { c: 0, r: currentRow - 1 }, e: { c: 5, r: currentRow - 1 } });
      currentRow += 1;
      
      const summaryData = [
        ['Total Products:', reportData.totalProducts],
        ['Total Quantity Sold:', reportData.totalQuantitySold],
        ['Total Revenue:', reportData.totalRevenue]
      ];
      
      summaryData.forEach((row, idx) => {
        XLSX.utils.sheet_add_aoa(ws, [row], { origin: `A${currentRow + idx}` });
        ws[`A${currentRow + idx}`].s = { font: { bold: true } };
        const cellRef = `B${currentRow + idx}`;
        if (ws[cellRef] && typeof row[1] === 'number') {
          if (idx === 2) {
            ws[cellRef].s = { numFmt: '"₹"#,##0.00' };
          } else {
            ws[cellRef].s = { numFmt: '#,##0' };
          }
        }
      });
      
      currentRow += summaryData.length + 2;
      
      XLSX.utils.sheet_add_aoa(ws, [['PRODUCT DETAILS']], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = ws[`!merges`] || [];
      ws[`!merges`].push({ s: { c: 0, r: currentRow - 1 }, e: { c: 5, r: currentRow - 1 } });
      currentRow += 1;
      
      const headers = ['S.No', 'Product Name', 'SKU', 'Category', 'Qty Sold', 'Total Amount'];
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${currentRow}` });
      
      headers.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: currentRow - 1, c: colIndex });
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center" }
        };
      });
      currentRow += 1;
      
      reportData.products.forEach((product, index) => {
        const row = [
          index + 1,
          product.productName || 'N/A',
          product.sku || 'N/A',
          product.categoryName || 'Uncategorized',
          product.quantitySold || 0,
          Number(product.totalAmount || 0)
        ];
        
        XLSX.utils.sheet_add_aoa(ws, [row], { origin: `A${currentRow}` });
        
        const amountCellRef = `F${currentRow}`;
        ws[amountCellRef].s = { numFmt: '"₹"#,##0.00' };
        
        currentRow += 1;
      });
      
      XLSX.utils.book_append_sheet(wb, ws, 'Top Selling Products');
      
      const filename = `Top_Selling_Products_Report_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(wb, filename, { compression: true });
      
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const paginatedProducts = reportData.products.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Layout>
      <Container maxWidth="xl" sx={reportPageSx.container}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={reportPageSx.breadcrumbs}
        >
          <Link
            component={RouterLink}
            to="/dashboard"
            underline="hover"
            color="inherit"
          >
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            to="/reports"
            underline="hover"
            color="inherit"
          >
            Reports
          </Link>
          <Typography color="text.primary">Top Selling Products</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, ...reportPageSx.pageHeader }}>
          <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, display: 'flex', alignItems: 'center', gap: 1, m: 0 }}>
            <TrendingUpIcon color="primary" />
            Top Selling Products
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExportToExcel}
            disabled={isExporting || reportData.products.length === 0}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={reportPageSx.filterCard}>
          <CardContent sx={reportPageSx.filterCardContent}>
            <Grid
              container
              spacing={reportPageSx.filterGrid.spacing}
              alignItems="flex-end"
              sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
            >
              <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="top-selling-location-label">Location</InputLabel>
                  <Select
                    labelId="top-selling-location-label"
                    label="Location"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {!locations.some(loc => loc.name?.toLowerCase().trim() === 'main location') && (
                      <MenuItem value="warehouse">Warehouse</MenuItem>
                    )}
                    {locations
                      .filter(location => {
                        const name = location.name?.toLowerCase().trim();
                        return name !== 'warehouse';
                      })
                      .map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0, flexShrink: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchReportData}
                  disabled={loading}
                  sx={{ ...reportPageSx.filterButton, whiteSpace: 'nowrap' }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Apply Filter'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Grid container spacing={reportPageSx.metricsGrid.spacing} sx={reportPageSx.metricsGrid}>
              <Grid item xs={12} sm={4}>
                <ReportMetricCard
                  label="Total Products"
                  value={reportData.totalProducts}
                  icon={<InventoryIcon />}
                  accentColor="primary.main"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReportMetricCard
                  label="Total Quantity Sold"
                  value={reportData.totalQuantitySold}
                  icon={<TrendingUpIcon />}
                  accentColor="info.main"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReportMetricCard
                  label="Total Revenue"
                  value={formatCurrency(reportData.totalRevenue)}
                  icon={<CurrencyRupeeIcon />}
                  accentColor="success.main"
                  valueColor="success.main"
                />
              </Grid>
            </Grid>

            <Card sx={reportPageSx.sectionCard}>
              <CardContent sx={reportPageSx.sectionCardContent}>
                <Typography sx={reportPageSx.sectionTitle}>
                  Top Selling Products
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>S.No</TableCell>
                        <TableCell>Product Name</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Qty Sold</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedProducts.length > 0 ? (
                        paginatedProducts.map((product, index) => (
                          <TableRow key={product.productId}>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>{product.productName || 'N/A'}</TableCell>
                            <TableCell>{product.sku || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={product.categoryName || 'Uncategorized'} 
                                size="small" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">{product.quantitySold || 0}</TableCell>
                            <TableCell align="right">{formatCurrency(product.totalAmount || 0)}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(product)}
                                color="primary"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No products found for the selected filters
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {reportData.products.length > rowsPerPage && (
                  <TablePagination
                    component="div"
                    count={reportData.products.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[rowsPerPage]}
                    size="small"
                  />
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Product Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={handleCloseDetailsDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Product Sales Details - {selectedProduct?.productName || 'N/A'}
              </Typography>
              <IconButton onClick={handleCloseDetailsDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedProduct && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Product Name</Typography>
                    <Typography variant="body1">{selectedProduct.productName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">SKU</Typography>
                    <Typography variant="body1">{selectedProduct.sku || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                    <Typography variant="body1">{selectedProduct.categoryName || 'Uncategorized'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Total Qty Sold</Typography>
                    <Typography variant="body1">{selectedProduct.quantitySold || 0}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Total Amount</Typography>
                    <Typography variant="body1">{formatCurrency(selectedProduct.totalAmount || 0)}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>Invoice Details</Typography>
                {loadingDetails ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : invoiceDetails.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Invoice Number</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Subtotal</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceDetails.map((detail, index) => (
                          <TableRow key={index}>
                            <TableCell>{detail.invoiceNumber || 'N/A'}</TableCell>
                            <TableCell>
                              {detail.orderDate ? format(new Date(detail.orderDate), 'dd-MM-yyyy') : 'N/A'}
                            </TableCell>
                            <TableCell>{detail.locationName || 'Warehouse'}</TableCell>
                            <TableCell align="right">{detail.quantity || 0}</TableCell>
                            <TableCell align="right">{formatCurrency(detail.unitPrice || 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(detail.subtotal || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                    No invoice details found for this product
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailsDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default TopSellingProductsReport;

